import { Link, createFileRoute } from "@tanstack/react-router";
import { GuideRoadmap, GuideStep, HomeFigure, MethodScorecardInfographic, PaymentJourneyInfographic } from "@/components/home-visuals";
import { ArrowRight, Clock, Scale, ShieldCheck, Wallet } from "lucide-react";

import { CasinoListWithCta } from "@/components/casino-list";
import { HomePaymentPicker, LatestPaymentNews } from "@/components/home-payment-discovery";
import { LiveTestData } from "@/components/live-test-data";
import { CoverageInfographic, DepositSpeedInfographic, PayoutSpeedInfographic, TestCoverageInfographic } from "@/components/infographics";
import { QualityGuarantee } from "@/components/quality-guarantee";
import { MethodLogo } from "@/components/method-logo";
import { PaymentCloud } from "@/components/payment-cloud";
import { Badge } from "@/components/ui/badge";
import { ProseSection } from "@/components/prose-section";
import { HOME_FAQ, HomeEditorial } from "@/components/home-editorial";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getChangelog, getPublicCasinos, getPublishedMethods } from "@/lib/queries";
import { SITE } from "@/lib/site";
import { ogImageMeta } from "@/lib/og";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Online Casino's per Betaalmethode Getest 2026 | Casino Betalingen" },
      {
        name: "description",
        content:
          "Vind de beste online casino's voor iDEAL, Trustly, PayPal en meer. Getest op stortingen, uitbetalingen, kosten en limieten. 18+.",
      },
      { property: "og:title", content: "Online Casino's per Betaalmethode Getest 2026 | Casino Betalingen" },
      {
        property: "og:description",
        content:
          "Ontdek de beste online casino's, getest op betaalmethoden, stortingssnelheid, uitbetalingen, kosten en limieten. 18+.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ...ogImageMeta("home"),
      { property: "og:url", content: `${SITE.url}/` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE.name,
          url: `${SITE.url}/`,
          inLanguage: "nl-NL",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "nl-NL",
          mainEntity: HOME_FAQ.map((f: { q: string; a: string }) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),

  component: HomePage,
});

const USP = [
  {
    icon: Wallet,
    title: "Per betaalmethode",
    text: "Van iDEAL tot e-wallets: bekijk per methode welke aanbieders haar echt ondersteunen.",
  },
  {
    icon: Clock,
    title: "Uitbetalingssnelheid",
    text: "Wij registreren hoe lang opnames duren, niet alleen hoe snel je kunt storten.",
  },
  {
    icon: Scale,
    title: "Onafhankelijk",
    text: "Rankings volgen vaste criteria. Adverteerders kunnen geen hogere positie kopen.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance voorop",
    text: "Alleen aanbieders die in Nederland zijn toegestaan. Strikte 18+-doelgroep.",
  },
];

function HomePage() {
  const methods = getPublishedMethods();
  const casinos = getPublicCasinos();
  const changelog = getChangelog(4);

  return (
    <main>
      <section className="border-b border-border bg-secondary/50">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 sm:py-14 lg:grid-cols-2">
          <div>
            <Badge variant="secondary" className="mb-3">
              Onafhankelijke vergelijking · 18+
            </Badge>
            <h1 className="max-w-3xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Online casino betaalmethoden: vind de beste casino's per <span className="text-primary">betaalmethode</span>
            </h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Hoe snel staat je storting op je account, en hoe snel krijg je winst weer uitbetaald?
              Wij meten betaalgegevens per aanbieder, van iDEAL tot Trustly en PayPal.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="bg-success text-success-foreground hover:bg-success/90">
                <Link to="/casinozoeker/">
                  Start de Casinozoeker
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/betaalmethoden/">Alle betaalmethoden</Link>
              </Button>
            </div>
          </div>
          <PaymentCloud />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6" aria-labelledby="topcasinos">
        <div className="mb-6">
          <h2 id="topcasinos" className="text-2xl font-bold tracking-tight text-foreground">
            Top 10 online casino&apos;s met de beste betaalmethoden
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            De eerste vijf aanbieders uit onze actuele top 10. Volgorde op basis van
            betaalopties, snelheid en kosten.
          </p>
        </div>
        <CasinoListWithCta casinos={casinos} limit={5} />
        <div className="mt-8">
          <HomeFigure index={0} />
        </div>
      </section>
      <GuideRoadmap />
      <GuideStep n={1} id="stap-testen" title="Eerst: waarom je deze lijst kunt vertrouwen">
        Voordat je een casino kiest, wil je weten hoe die top 10 tot stand komt. Hieronder zie je precies wat we testen en wie het controleert.
      </GuideStep>
      <section className="mx-auto max-w-7xl px-4 pb-4 pt-8 sm:px-6">
        <QualityGuarantee />
      </section>
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" aria-labelledby="usp">
        <h2 id="usp" className="sr-only">
          Waarom CasinoBetalingen.com
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {USP.map((item) => (
            <Card key={item.title} className="card-surface">
              <CardContent className="p-5">
                <item.icon className="h-6 w-6 text-primary" aria-hidden />
                <h3 className="mt-3 font-semibold text-foreground">{item.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{item.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
      <GuideStep n={2} id="stap-kiezen" title="Kies de betaalmethode die bij jou past">
        Nu je weet hoe we werken, de belangrijkste vraag: hoe wil jij storten en uitbetalen? Begin bij je eigen bank of wallet, niet bij het casino.
      </GuideStep>
      <HomePaymentPicker />
      <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6" aria-labelledby="methoden">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 id="methoden" className="text-2xl font-bold tracking-tight text-foreground">
              Populaire betaalmethoden
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {methods.length} methoden vergeleken, laatst gecontroleerd op{" "}
              {formatDate(methods[0]?.last_verified_at)}.
            </p>
          </div>
          <Button asChild variant="ghost" className="hidden sm:inline-flex">
            <Link to="/betaalmethoden/">
              Bekijk alle
              <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {methods.slice(0, 6).map((m) => (
            <Link key={m.id} to="/betaalmethoden/$slug/" params={{ slug: m.slug }} className="group">
              <Card className="card-surface h-full transition-colors group-hover:border-primary/40">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <MethodLogo slug={m.slug} name={m.name} />
                    <Badge variant="outline" className="capitalize">
                      {m.category}
                    </Badge>
                  </div>
                  <h3 className="sr-only">{m.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{m.short_description}</p>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Storten: {m.typical_deposit_speed} · Opnemen:{" "}
                    {m.withdrawal_supported ? m.typical_withdrawal_speed : "Niet beschikbaar"}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>
      <ProseSection id="uitleg" title="Zo gebruik je deze vergelijking">
        <p>
          Welke aanbieder het best bij je past, hangt vooral af van hoe je betaalt. Wil je storten
          vanaf je eigen bankrekening, dan is{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "ideal" }}>iDEAL</Link> in Nederland de
          snelste en meest vertrouwde route: in onze tests stond het geld mediaan binnen zeven
          seconden op de spelersrekening. Wil je winst juist via dezelfde methode terugkrijgen, dan
          kijk je beter naar{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "trustly" }}>Trustly</Link> of een
          e-wallet als <Link to="/betaalmethoden/$slug/" params={{ slug: "skrill" }}>Skrill</Link>,
          omdat die wél als retourmethode werken.
        </p>
        <p>
          Speel je liever met een vast budget, dan is de prepaid route via{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "paysafecard" }}>Paysafecard</Link> een
          logische keuze; uitbetalen gaat dan alsnog via je bank. Op mobiel zijn{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "apple-pay" }}>Apple Pay</Link> en{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "google-pay" }}>Google Pay</Link> de
          snelste kassa-opties, omdat je de betaling met gezichtsherkenning of vingerafdruk
          bevestigt.
        </p>
        <p>
          Weet je al welke methode je gebruikt, ga dan direct naar de{" "}
          <Link to="/casinozoeker/">casinozoeker</Link> en filter op stortlimiet, kosten en
          opnamemogelijkheid. Wil je eerst per methode de meetgegevens en de kleine lettertjes
          doorlezen, dan vind je die op de pagina met{" "}
          <Link to="/betaalmethoden/">alle betaalmethoden</Link>. Wie de tests uitvoert en
          controleert, lees je bij <Link to="/auteurs/">onze redactie</Link>.
        </p>
      </ProseSection>
      <GuideStep n={3} id="stap-route" title="Zo loopt je geld van storting tot uitbetaling">
        Gekozen? Dan helpt het om te weten wat er na je klik gebeurt. Dit zijn de vier momenten waarop je geld onderweg is, en waar het kan blijven hangen.
      </GuideStep>
      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6">
        <PaymentJourneyInfographic />
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-6 sm:px-6">
        <HomeFigure index={1} />
      </section>
      <GuideStep n={4} id="stap-data" title="Controleer de cijfers zelf">
        Geloof ons niet op ons woord. Dit zijn de meetgegevens achter de ranglijst, per methode naast elkaar.
      </GuideStep>
      <section className="mx-auto max-w-7xl px-4 py-4 sm:px-6" aria-labelledby="onderzoek">
        <h2 id="onderzoek" className="text-2xl font-bold tracking-tight text-foreground">
          Ons betaalonderzoek in beeld
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Alle cijfers hieronder komen uit onze eigen meetreeks: echte stortingen en opnames met eigen
          geld, per methode en per aanbieder vastgelegd.
        </p>
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <DepositSpeedInfographic />
          <PayoutSpeedInfographic />
        </div>
        <div className="mt-6">
          <MethodScorecardInfographic />
        </div>
        <div className="mt-6 grid gap-6">
          <TestCoverageInfographic />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <LiveTestData />
        <div className="mt-8">
          <CoverageInfographic />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-6 sm:px-6">
        <HomeFigure index={2} />
      </section>
      <GuideStep n={5} id="stap-verdieping" title="Verdieping: kosten, veiligheid en veelgestelde vragen">
        Wil je het naadje van de kous weten? Hier lees je alles over limieten, verificatie en verantwoord spelen.
      </GuideStep>
      <HomeEditorial />

      <LatestPaymentNews />


      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" aria-labelledby="updates">
        <h2 id="updates" className="text-2xl font-bold tracking-tight text-foreground">
          Laatste controles
        </h2>
        <ul className="mt-5 space-y-3">
          {changelog.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4 text-sm sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="text-foreground">{entry.change_summary}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatDate(entry.published_at)} · {entry.checked_by}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
